<template>
  <div class="profile-overlay" role="presentation" @click.self="requestClose">
    <section
      ref="dialogRef"
      class="profile-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Student grade history"
      tabindex="-1"
      @keydown="handleDialogKeydown"
    >
      <div class="profile-dialog__header">
        <div>
          <p>Student profile</p>
          <h3>{{ profile.student.display_name || profile.student.username }} · Grade history</h3>
        </div>
        <button ref="closeButtonRef" class="ui-button compact-button" type="button" @click="requestClose">Close</button>
      </div>

      <div v-if="profile.grades.length" class="profile-grade-list">
        <article v-for="row in profile.grades" :key="row.assignment.id">
          <div>
            <strong>{{ row.assignment.title }}</strong>
            <StatusPill :value="row.status" />
          </div>
          <span>AI {{ row.ai_grade ?? 'Not graded' }} · Teacher {{ row.teacher_grade ?? 'Not graded' }}</span>
        </article>
      </div>
      <div v-else class="ui-empty">No grade history is available for this student.</div>
    </section>
  </div>
</template>

<script setup>
import { nextTick, onMounted, ref } from 'vue'
import StatusPill from './StatusPill.vue'

defineProps({
  profile: { type: Object, required: true },
})

const emit = defineEmits(['close'])
const dialogRef = ref(null)
const closeButtonRef = ref(null)
const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function requestClose() {
  emit('close')
}

function handleDialogKeydown(event) {
  if (event.key === 'Escape') {
    event.preventDefault()
    requestClose()
    return
  }
  if (event.key !== 'Tab') return

  const focusableElements = Array.from(dialogRef.value?.querySelectorAll(focusableSelector) || [])
  if (!focusableElements.length) {
    event.preventDefault()
    dialogRef.value?.focus()
    return
  }

  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]
  if (event.shiftKey && (document.activeElement === firstElement || !dialogRef.value?.contains(document.activeElement))) {
    event.preventDefault()
    lastElement.focus()
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault()
    firstElement.focus()
  }
}

onMounted(async () => {
  await nextTick()
  ;(closeButtonRef.value || dialogRef.value)?.focus()
})
</script>

<style scoped>
.profile-overlay {
  position: fixed;
  z-index: 10;
  inset: 0;
  display: grid;
  place-items: center;
  padding: var(--space-4);
  background: rgba(24, 29, 31, 0.42);
}

.profile-dialog {
  width: min(100%, 42rem);
  max-height: min(44rem, calc(100vh - 2rem));
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius-surface);
  padding: var(--space-6);
  background: var(--surface);
  box-shadow: 0 1.5rem 4rem rgba(24, 29, 31, 0.2);
}

.profile-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  border-bottom: 1px solid var(--border);
  padding-bottom: var(--space-4);
}

.profile-dialog__header p,
.profile-dialog__header h3 {
  margin: 0;
}

.profile-dialog__header p {
  color: var(--accent-strong);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.profile-dialog__header h3 {
  margin-top: var(--space-1);
  font-family: var(--font-academic);
  font-size: 1.25rem;
}

.profile-grade-list {
  display: grid;
}

.profile-grade-list article {
  display: grid;
  gap: var(--space-2);
  border-bottom: 1px solid var(--border);
  padding: var(--space-4) 0;
}

.profile-grade-list article:last-child {
  border-bottom: 0;
}

.profile-grade-list article div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}

.profile-grade-list article > span {
  color: var(--ink-muted);
  font-size: 0.875rem;
}

.compact-button {
  flex: none;
  min-height: 2rem;
  padding: var(--space-1) var(--space-2);
  font-size: 0.8125rem;
}

@media (max-width: 767px) {
  .profile-dialog {
    padding: var(--space-4);
  }

  .profile-dialog__header {
    align-items: stretch;
    flex-direction: column;
  }

  .compact-button {
    min-width: 44px;
    min-height: 44px;
  }

  .profile-grade-list article div {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
